import { useMemo } from "react";
import axios from "axios";
import { Method } from "axios";
import { AxiosResponse } from "axios";
import { useAuth } from "../modules/auth/hooks/useAuth";
import { useNotify } from "../modules/notify/useNotify";
import { mutateErrors } from "../utils";
import { NotifyMode } from "../modules/notify/NotifyModeEnum";

interface ApiRequestOptions {
  method: Method;
  url: string;
  data?: any;
  headers?: any[];
  accessToken?: string;
  recaptchaToken?: string;
  shouldHandleError?: boolean;
}

export function useApi() {
  const { logout } = useAuth();
  const snackNotify = useNotify();

  return useMemo(
    () => ({
      async request({
        url,
        method,
        headers,
        data,
        accessToken,
        recaptchaToken,
        shouldHandleError,
      }: ApiRequestOptions): Promise<AxiosResponse | undefined> {
        const dataOrParams = "GET" === method.toUpperCase() ? "params" : "data";
        const token = accessToken ? accessToken : localStorage.getItem("token");

        const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

        try {
          return await axios.request({
            url,
            method,
            headers: {
              ...authHeader,
              Accept: "application/json",
              ContentType: "application/json",
              ...(recaptchaToken && {
                "X-Recaptcha-Token": recaptchaToken,
              }),
              ...headers,
            },
            [dataOrParams]: data,
          });
        } catch (error) {
          if (error.response && error.response.status === 401) {
            logout?.();
          } else if (shouldHandleError) {
            const { message } = mutateErrors(error);
            snackNotify("Error", message, NotifyMode.ERROR);
            throw error;
          } else throw error;
        }
      },
    }),
    [logout],
  );
}
